import React, { type ReactNode } from "react";
import cx from "classnames";
import {
  Avatar,
  Button,
  Caret,
  ChipIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RollingCounter,
  Skeleton,
  StopIcon,
  Typography,
} from "@picklebet/ui";
import { useGameSessions } from "@/features/casino/hooks/useGameSessions";
import { Link } from "@/features/shell/components/shared/Link/Link";
import { AnimatePresence, motion } from "motion/react";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { useCashAvailable } from "@/hooks/firestore/user/useAccountBalances";
import { constructCurrency } from "common/currency";

import * as styles from "./QuickBalance.module.scss";

type QuickBalanceProps = React.HTMLAttributes<HTMLDivElement> & {
  name?: string;
  showAvatar?: boolean;
  loading?: boolean;
  className?: string;
  avatarSrc?: string;
  linkPath?: string;
  canHavePopover?: boolean;
};

const QuickBalanceSkeleton = ({ className }: { className?: string }) => {
  return (
    <div data-user-pill className={cx(styles.container, className)}>
      <span data-user-pill-avatar className={styles.avatar}>
        <Skeleton
          className={styles.avatarSkeleton}
          shape="circle"
          mode="light"
        />
      </span>
      <span className={styles.balance}>
        <span>
          <Skeleton className={styles.text} edge="hard" mode="light" />
        </span>
        <span className={styles.promo}>
          <Skeleton className={styles.text} edge="hard" mode="light" />
        </span>
      </span>
    </div>
  );
};

const LinkOrPopover = ({
  to,
  isPopover,
  children,
}: {
  to?: string;
  isPopover: boolean;
  children: ReactNode;
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const setModal = useShellStore((state) => state.setModal);

  const handleShowEndSessionConfirmationModal = () => {
    setIsOpen(false);
    setModal("endGameSession");
  };

  const handleShowDepositModal = () => {
    setIsOpen(false);
    setModal("deposit");
  };

  if (isPopover) {
    return (
      <Popover open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)}>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          arrowClassName={styles.popoverArrow}
          className={styles.popoverContent}
          showArrow
          align="end"
        >
          <div className={styles.popoverContentInner}>
            <div className={styles.header}>
              <div className={styles.iconContainer}>
                <ChipIcon width={28} height={28} />
              </div>
              <div
                className={cx(styles.iconContainer, styles.iconContainerLight)}
              >
                <StopIcon width={18} height={18} />
              </div>
            </div>
            <Typography variant="h5" noSpacing className={styles.typography}>
              Deposit, or end your active Casino session to access your funds to
              bet and withdraw
            </Typography>
          </div>
          <div className={styles.actions}>
            <div className={styles.divider}></div>
            <div className={styles.btnContainer}>
              <Button onClick={handleShowDepositModal} size="sm">
                Deposit
              </Button>
              <Button onClick={handleShowEndSessionConfirmationModal} size="sm">
                End Session
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  if (to) {
    return <Link to={to}>{children}</Link>;
  }

  return children;
};

const QuickBalance = ({
  name = "QuickBalance avatar",
  showAvatar = true,
  loading = false,
  className,
  avatarSrc = "",
  linkPath,
  canHavePopover = false,
}: QuickBalanceProps) => {
  const { hasActiveGameSession, isLoading: isLoadingGameSessions } =
    useGameSessions(true);
  const {
    available,
    promo,
    currency,
    isLoading: isLoadingBalance,
  } = useCashAvailable();

  const showPromo = promo > 0;
  if (loading || isLoadingGameSessions || isLoadingBalance) {
    return <QuickBalanceSkeleton className={className} />;
  }

  return (
    <LinkOrPopover
      to={linkPath}
      isPopover={canHavePopover && hasActiveGameSession}
    >
      <div
        data-user-pill
        className={cx(styles.container, className, {
          [styles.inPlayContainer]: hasActiveGameSession,
        })}
      >
        {showAvatar && (
          <span data-user-pill-avatar className={styles.avatar}>
            <Avatar alt={name} name={name} size={24} src={avatarSrc} />
          </span>
        )}
        <AnimatePresence mode="wait">
          {hasActiveGameSession ? (
            <motion.div
              key="in-play"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
            >
              <span className={styles.inPlayInner}>
                <span>In Play</span>
                {canHavePopover && (
                  <Caret variant="down" className={styles.caret} />
                )}
              </span>
            </motion.div>
          ) : (
            <motion.div
              key="balance"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
            >
              <span className={styles.balance}>
                <span
                  className={cx({
                    [styles.balanceWithoutPromo]: !showPromo,
                  })}
                >
                  <RollingCounter
                    value={available}
                    formatValue={(v) =>
                      constructCurrency(v, {
                        currencyType: currency,
                        showDecimals: true,
                        showCurrencySymbol: true,
                      })
                    }
                  />
                </span>
                {promo > 0 && (
                  <motion.span
                    key="promo"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className={styles.promo}
                  >
                    <RollingCounter
                      value={promo}
                      formatValue={(v) =>
                        constructCurrency(v, {
                          currencyType: currency,
                          showDecimals: true,
                          showCurrencySymbol: true,
                        })
                      }
                    />
                  </motion.span>
                )}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </LinkOrPopover>
  );
};

export { QuickBalance };
